import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import AppSettings from '../app-settings';
import { handleNetworkLayerFailure } from '../utilities/network-failure';

const GRAPHQL_URI = `${AppSettings.GRAPHQL_SERVER_URL}graphql`;

const fetcher = (input: RequestInfo | URL, init?: RequestInit) => window.fetch(input, init);

const requestLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem('token');
  const customHeaders = operation.getContext().headers || {};

  if (token) {
    operation.setContext({
      headers: {
        ...customHeaders,
        authorization: `Bearer ${token}`,
      },
    });
  }

  return forward(operation);
});

const client = new ApolloClient({
  connectToDevTools: AppSettings.ENVIRONMENT_NAME === 'local',
  link: ApolloLink.from([
    onError(({ networkError, operation }) => {
      if (handleNetworkLayerFailure({ networkError, operation, url: GRAPHQL_URI })) return;
      if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('loggedIn');
      }
    }),
    requestLink,
    createHttpLink({
      uri: GRAPHQL_URI,
      fetch: fetcher,
      credentials: 'include',
    }),
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Inventory: {
        merge: true,
      },
      OfferListingType: {
        merge: true,
        fields: {
          // Never let an incoming null overwrite a known inventory.
          inventory: {
            merge: (existing, incoming) => incoming ?? existing,
          },
        },
      },
      offerShiftOriginalOLType: {
        keyFields: false,
      },
      Query: {
        fields: {
          listedInventoriesQuery: {
            keyArgs: false,
            merge(existing = [], incoming) {
              const newList = [...existing, ...(incoming || [])];
              const invMap = newList.reduce((iMap, inv) => {
                iMap.set(inv.__ref, inv);
                return iMap;
              }, new Map<string, { __ref: string }>());
              return [...invMap.values()];
            },
          },
          trucklaneViewQuery: {
            keyArgs: false,
            merge(existing = { trucklanes: [] }, incoming, { args }) {
              // A successful full fetch (getOutOfSyncChannels: false) is authoritative — replace, so
              // lanes the server no longer returns don't linger as phantom entries. Skip the replace
              // when the response carries errors (empty trucklanes) so a transient error doesn't blank
              // the view. The recalc path refetches with getOutOfSyncChannels: true, a partial set of
              // just the out-of-sync lanes, which we fold into the existing full set (concat + dedupe).
              if (args?.getOutOfSyncChannels === false && !incoming.errors?.length) return incoming;
              const array = [...existing.trucklanes, ...(incoming.trucklanes || [])];
              const set = new Set();
              const trucklanes = array.filter((item) => {
                const id = item.__ref;
                return set.has(id) ? false : set.add(id);
              });
              return { ...incoming, trucklanes };
            },
          },
        },
      },
    },
  }),
});

export default client;
